import { deleteOfflineDb, openOfflineDb } from './idb';
import { clearOutbox } from './outbox';

/** Every store of the offline database (see openOfflineDb). */
const OFFLINE_STORES = ['outbox', 'kv', 'siteSnapshots'] as const;

/**
 * Empty every offline store. Works even while other connections are open - unlike deleteDatabase, which the
 * browser only completes once all connections of the running app are closed.
 */
async function clearOfflineStores(platform: string): Promise<void> {
  const db = await openOfflineDb(platform);
  try {
    const names = OFFLINE_STORES.filter((s) => db.objectStoreNames.contains(s));
    if (names.length === 0) return;
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(names, 'readwrite');
      for (const name of names) tx.objectStore(name).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error || new Error('clear offline stores failed'));
      tx.onabort = () => reject(tx.error || new Error('clear offline stores aborted'));
    });
  } finally {
    db.close();
  }
}

/**
 * On logout, remove everything this account left in the browser's offline database: queued actions AND the cached
 * data (conversation lists, pages, site snapshots). Before 2026-10-11 only the queue was cleared, so a shared
 * computer kept the previous user's cached chats after logout (found while writing the Platzel privacy texts).
 * `keepCache` keeps the old queue-only behaviour for callers that explicitly need it.
 */
export async function clearOfflineOnLogout(
  platform: string,
  opts: { wipeDatabase?: boolean; keepCache?: boolean } = {},
): Promise<void> {
  if (opts.keepCache) {
    await clearOutbox(platform);
    return;
  }
  try {
    await clearOfflineStores(platform);
  } catch {
    await clearOutbox(platform).catch(() => undefined);
  }
  await deleteOfflineDb(platform).catch(() => undefined);
}
